import { useState } from 'react';

export default function FooterSection() {
  const [email, setEmail] = useState('');
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email) return;

    setSubmitStatus('loading');

    try {
      const formData = new URLSearchParams();
      formData.append('email', email);

      const response = await fetch('https://readdy.ai/api/form/d64pvtckdgthdql6tj5g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setEmail('');
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setTimeout(() => setSubmitStatus('idle'), 3000);
    }
  };

  return (
    <footer id="contact" className="relative bg-black">
      {/* Trust Banner */}
      <div className="w-full bg-neutral-100 py-8 border-b border-neutral-200">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-16">
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-8 text-center">
            <div className="flex items-center gap-3">
              <i className="ri-shield-check-line text-2xl text-black"></i>
              <span className="font-body text-[14px] text-black font-semibold">
                三鑑所 Tricore
              </span>
            </div>
            <div className="hidden md:block w-px h-6 bg-neutral-300"></div>
            <p className="font-body text-[13px] text-neutral-700 leading-relaxed">
              結合 Entrupy AI、Legit App 與中檢標準，為您的資產把關。支持全國複檢。
            </p>
          </div>
        </div>
      </div>

      {/* Top Divider */}
      <div className="w-full h-px bg-white/[0.08]"></div>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-16 py-20">
        {/* Top Row — Brand + Newsletter */}
        <div className="grid lg:grid-cols-2 gap-16 mb-16 pb-16 border-b border-white/[0.08]">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <img
                src="https://static.readdy.ai/image/3b1a2dd7b171b6e7ce06ff7b01b788f6/1cebd8518503bbfa22b098278f31dba1.jpeg"
                alt="TRICORE 三鑑所"
                className="h-14 w-14 rounded-full object-cover"
              />
              <div className="flex flex-col">
                <span className="font-body text-[11px] tracking-[0.15em] uppercase text-white/40">
                  TRICORE
                </span>
                <span className="font-heading text-[18px] font-bold tracking-wider text-white">
                  三鑑所
                </span>
              </div>
            </div>
            <p className="font-body text-[13px] text-white/50 leading-[1.8] max-w-sm mb-6">
              名牌包・翡翠・寶石的客觀鑑定權威。講憑據，不講故事。
            </p>
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center gap-2 px-3 py-2 bg-white/5 border border-white/10">
                <i className="ri-robot-2-line text-white/60"></i>
                <span className="font-body text-[11px] text-white/60 tracking-wider">Entrupy AI</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-2 bg-white/5 border border-white/10">
                <i className="ri-smartphone-line text-white/60"></i>
                <span className="font-body text-[11px] text-white/60 tracking-wider">Legit App</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-2 bg-white/5 border border-white/10">
                <i className="ri-verified-badge-line text-white/60"></i>
                <span className="font-body text-[11px] text-white/60 tracking-wider">中檢標準</span>
              </div>
            </div>
          </div>

          {/* Newsletter */}
          <div className="lg:pl-16">
            <h4 className="text-[11px] font-body font-medium tracking-[0.25em] text-white/40 uppercase mb-4">
              <a href="#contact">訂閱電子報</a>
            </h4>
            <p className="font-body text-[13px] text-white/50 mb-5">
              獲取最新鑑定資訊與優惠活動
            </p>
            <form
              id="newsletter-form"
              onSubmit={handleSubmit}
              className="flex gap-3"
              data-readdy-form
            >
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="您的電子郵件"
                className="flex-1 px-4 py-3 bg-transparent border border-white/10 text-[13px] text-white placeholder-white/30 focus:outline-none focus:border-white/30 transition-colors font-body"
                required
              />
              <button
                type="submit"
                disabled={submitStatus === 'loading'}
                className="px-5 py-3 bg-white text-black hover:bg-white/90 transition-colors flex items-center justify-center cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                {submitStatus === 'loading' ? (
                  <i className="ri-loader-4-line text-lg animate-spin"></i>
                ) : (
                  <i className="ri-arrow-right-line text-lg"></i>
                )}
              </button>
            </form>
            {submitStatus === 'success' && (
              <p className="text-[12px] text-white/60 mt-3 flex items-center gap-1 font-body">
                <i className="ri-checkbox-circle-line"></i>
                訂閱成功！感謝您的支持
              </p>
            )}
            {submitStatus === 'error' && (
              <p className="text-[12px] text-red-400 mt-3 flex items-center gap-1 font-body">
                <i className="ri-error-warning-line"></i>
                訂閱失敗，請稍後再試
              </p>
            )}
          </div>
        </div>

        {/* Bottom Row — Links + Social */}
        <div className="grid md:grid-cols-12 gap-8">
          {/* Links */}
          <div className="md:col-span-3">
            <h4 className="text-[11px] font-body font-medium tracking-[0.2em] text-white/30 uppercase mb-5">
              <a href="#services">核心服務</a>
            </h4>
            <ul className="space-y-3 font-body text-[13px]">
              <li><a href="#services" className="text-white/50 hover:text-white transition-colors cursor-pointer">鑑寶 LAB</a></li>
              <li><a href="#services" className="text-white/50 hover:text-white transition-colors cursor-pointer">炅白 Jiong Bai</a></li>
              <li><a href="#services" className="text-white/50 hover:text-white transition-colors cursor-pointer">映白 Ying Bai</a></li>
              <li><a href="#process" className="text-white/50 hover:text-white transition-colors cursor-pointer">鑑定流程</a></li>
            </ul>
          </div>

          <div className="md:col-span-3">
            <h4 className="text-[11px] font-body font-medium tracking-[0.2em] text-white/30 uppercase mb-5">
              <a href="#about">關於我們</a>
            </h4>
            <ul className="space-y-3 font-body text-[13px]">
              <li><a href="#about" className="text-white/50 hover:text-white transition-colors cursor-pointer">品牌理念</a></li>
              <li><a href="#about" className="text-white/50 hover:text-white transition-colors cursor-pointer">專業團隊</a></li>
              <li><a href="#appointment" className="text-white/50 hover:text-white transition-colors cursor-pointer">預約鑑定</a></li>
              <li><a href="#contact" className="text-white/50 hover:text-white transition-colors cursor-pointer">聯絡我們</a></li>
            </ul>
          </div>

          <div className="md:col-span-3">
            <h4 className="text-[11px] font-body font-medium tracking-[0.2em] text-white/30 uppercase mb-5">
              聯絡方式
            </h4>
            <ul className="space-y-3 font-body text-[13px]">
              <li className="text-white/50">info@tricore.tw</li>
              <li className="text-white/50">LINE: @tricore</li>
              <li className="text-white/50">週一至週五 10:00-18:00</li>
            </ul>
          </div>

          {/* Social */}
          <div className="md:col-span-3 flex md:justify-end items-start">
            <div className="flex items-center gap-3">
              <a
                href="#"
                className="w-10 h-10 border border-white/10 flex items-center justify-center hover:border-white/30 transition-colors cursor-pointer"
                rel="nofollow"
              >
                <i className="ri-line-line text-lg text-white/50 hover:text-white transition-colors"></i>
              </a>
              <a
                href="#"
                className="w-10 h-10 border border-white/10 flex items-center justify-center hover:border-white/30 transition-colors cursor-pointer"
                rel="nofollow"
              >
                <i className="ri-instagram-line text-lg text-white/50 hover:text-white transition-colors"></i>
              </a>
              <a
                href="#"
                className="w-10 h-10 border border-white/10 flex items-center justify-center hover:border-white/30 transition-colors cursor-pointer"
                rel="nofollow"
              >
                <i className="ri-mail-line text-lg text-white/50 hover:text-white transition-colors"></i>
              </a>
            </div>
          </div>
        </div>

        {/* Copyright */}
        <div className="mt-16 pt-8 border-t border-white/[0.08] flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="font-body text-[11px] text-white/30">
            © 2025 三鑑所 Tricore All rights reserved.
          </p>
          <a
            href="https://readdy.ai/?ref=logo"
            target="_blank"
            rel="noopener noreferrer"
            className="font-body text-[11px] text-white/30 hover:text-white/50 transition-colors cursor-pointer"
          >
            Powered by Readdy
          </a>
        </div>
      </div>
    </footer>
  );
}
